// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import ConversationFlow from './ConversationFlow';
import { generatePersonalizedBoard } from './utils/boardGenerator';
import { updateStreakFirestore } from './utils/achievementUtils';

// Map new questionnaire answers → boardGenerator input shape
const mapQuestionnaireToBoardProfile = (q) => ({
  mainStruggle:
    q.primaryChallenge === 'career_money'  ? ['money'] :
    q.primaryChallenge === 'relationships' ? ['boundaries'] :
    q.primaryChallenge === 'inner_peace'   ? ['shame'] : ['purpose'],
  primaryGoal: [q.primaryChallenge],
  procrastinationLevel: q.shadowPattern === 'fear_avoidance' ? 8 : q.shadowPattern === 'perfectionism' ? 7 : 4,
  selfWorthLevel:       q.shadowPattern === 'perfectionism'  ? 3 : q.shadowPattern === 'numbness' ? 4 : 6,
  deservingLevel:       q.primaryChallenge === 'career_money' ? 3 : 6,
  moneyFeeling:         q.primaryChallenge === 'career_money' ? 'difficult' : 'neutral',
  consistencyIssue:     q.shadowPattern === 'fear_avoidance' ? ['self-sabotage'] : [],
  spiritualBackground:  ({
    just_starting: 'beginner', some_experience: 'intermediate',
    regular_practitioner: 'practitioner', deep_seeker: 'advanced',
  })[q.spiritualStage] ?? 'beginner',
});

const DEFAULT_PROFILE = {
  mainStruggle: ['purpose'], primaryGoal: ['purpose'],
  procrastinationLevel: 5, selfWorthLevel: 5, deservingLevel: 5,
  moneyFeeling: 'neutral', consistencyIssue: [], spiritualBackground: 'beginner',
};

const DOT_PATTERNS = {
  1: [0,0,0, 0,1,0, 0,0,0],
  2: [0,0,1, 0,0,0, 1,0,0],
  3: [0,0,1, 0,1,0, 1,0,0],
  4: [1,0,1, 0,0,0, 1,0,1],
  5: [1,0,1, 0,1,0, 1,0,1],
  6: [1,0,1, 1,0,1, 1,0,1],
};

function DiceFace({ value }) {
  const dots = DOT_PATTERNS[value] || DOT_PATTERNS[1];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(3, 1fr)',
      width: '78px', height: '78px', padding: '12px',
      gap: '5px', boxSizing: 'border-box',
    }}>
      {dots.map((filled, i) => (
        <div key={i} style={{
          borderRadius: '50%',
          background: filled ? '#C9920A' : 'rgba(201,146,10,0.1)',
          transition: 'background 0.05s',
        }} />
      ))}
    </div>
  );
}

export default function DiceRoll({
  boardProfile,
  hasPersonalizedBoard,
  currentSquare,
  onPracticeComplete,
  onOpenDashboard,
  onGoToQuestionnaire,
  onOpenReflection,
}) {
  const { currentUser, signOut } = useAuth();
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const [position, setPosition]               = useState(currentSquare ?? 0);
  const [rollResult, setRollResult]           = useState(null);
  const [landedSquare, setLandedSquare]       = useState(null);
  const [isRolling, setIsRolling]             = useState(false);
  const [theme, setTheme]                     = useState(null);
  const [activeMechanic, setActiveMechanic]   = useState(null);
  const [showConversation, setShowConversation] = useState(false);
  const [showDesignPrompt, setShowDesignPrompt] = useState(false);
  const [personalizedBoard, setPersonalizedBoard] = useState(null);
  const [isLoadingBoard, setIsLoadingBoard]   = useState(true);
  const [streak, setStreak]                   = useState(0);
  const [rollingFace, setRollingFace]         = useState(1);

  // Cycle dice face rapidly while rolling
  useEffect(() => {
    if (!isRolling) return;
    const id = setInterval(() => setRollingFace(f => (f % 6) + 1), 80);
    return () => clearInterval(id);
  }, [isRolling]);

  // Sync position when prop changes (returning user load)
  useEffect(() => { setPosition(currentSquare ?? 0); }, [currentSquare]);

  // Load or generate board
  useEffect(() => {
    const initBoard = async () => {
      setIsLoadingBoard(true);
      try {
        if (currentUser) {
          const snap = await getDoc(doc(db, 'users', currentUser.uid, 'board', 'data'));
          if (snap.exists() && snap.data()?.squares?.length) {
            setPersonalizedBoard(snap.data());
            setIsLoadingBoard(false);
            return;
          }
        }
        // Generate new board
        const profile = hasPersonalizedBoard && boardProfile
          ? mapQuestionnaireToBoardProfile(boardProfile)
          : DEFAULT_PROFILE;
        const board = generatePersonalizedBoard(profile);
        setPersonalizedBoard(board);
        if (currentUser) {
          setDoc(doc(db, 'users', currentUser.uid, 'board', 'data'), {
            ...board, generatedAt: new Date().toISOString(),
          }, { merge: true }).catch(console.error);
        }
      } catch (err) {
        console.error('Board init error:', err);
        const fallback = generatePersonalizedBoard(DEFAULT_PROFILE);
        setPersonalizedBoard(fallback);
      } finally {
        setIsLoadingBoard(false);
      }
    };
    initBoard();
  }, [boardProfile, hasPersonalizedBoard, currentUser]);

  // Load streak from Firestore
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'))
      .then(snap => { if (snap.exists()) setStreak(snap.data().streakCount ?? 0); })
      .catch(console.error);
  }, [currentUser]);

  const themes = React.useMemo(() => {
    if (!personalizedBoard?.squares) return {};
    return personalizedBoard.squares.reduce((acc, sq) => {
      if (sq?.position) {
        acc[sq.position] = {
          title: sq.title || 'Practice',
          desc: sq.description || sq.desc || 'Practice mindfulness',
          type: sq.type || 'practice',
          phase: sq.phase || 'foundation',
        };
      }
      return acc;
    }, {});
  }, [personalizedBoard]);

  const rollDice = () => {
    if (isRolling || !personalizedBoard) return;
    setIsRolling(true);
    setRollResult(null);
    setTheme(null);
    setActiveMechanic(null);

    const tid = setTimeout(() => {
      if (!isMounted.current) return;
      const roll = Math.floor(Math.random() * 6) + 1;
      const candidate = position + roll;
      const landed = candidate > 100 ? position : candidate; // no overshoot

      const mechanic = (personalizedBoard.snakes_and_ladders ?? []).find(
        m => (m.fromSquare ?? m.from) === landed
      ) ?? null;

      setRollResult(roll);
      setLandedSquare(landed);
      setPosition(landed);
      setTheme(themes[landed] ?? { title: `Square ${landed}`, desc: 'Continue your practice', type: 'practice', phase: 'foundation' });
      if (mechanic) setActiveMechanic(mechanic);
      setIsRolling(false);
      // Persist new position immediately so refresh doesn't lose progress
      if (currentUser) {
        setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
          currentSquare: landed,
        }, { merge: true }).catch(console.error);
      }
    }, 900);
  };

  const handleConversationClose = async () => {
    setShowConversation(false);
    setRollResult(null);
    setTheme(null);
    if (!landedSquare) return;

    try {
      const streakData = await updateStreakFirestore(currentUser.uid, db, doc, setDoc, getDoc);
      await setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
        currentSquare: landedSquare,
        streakCount: streakData.streakCount,
        lastPracticeDate: new Date().toISOString(),
      }, { merge: true });
      setPosition(landedSquare);
      setStreak(streakData.streakCount);
      onPracticeComplete(landedSquare, streakData);
      if (!hasPersonalizedBoard) setShowDesignPrompt(true);
    } catch (err) {
      console.error('Post-practice save error:', err);
      setPosition(landedSquare);
      onPracticeComplete(landedSquare, { streakCount: streak, milestonesAwarded: [] });
    }
  };

  const getPhaseLabel = (pos) => {
    if (pos <= 25) return 'Foundation';
    if (pos <= 50) return 'Primary Healing';
    if (pos <= 75) return 'Integration';
    return 'Service & Abundance';
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoadingBoard) {
    return (
      <>
        <div style={styles.bg} />
        <div style={styles.loadingCenter}>
          <div style={{ fontSize: '52px', marginBottom: '16px' }}>🎲</div>
          <p style={styles.loadingText}>Preparing your journey...</p>
        </div>
      </>
    );
  }

  // ── Conversation ──────────────────────────────────────────────────────────
  if (showConversation && landedSquare && theme) {
    return (
      <ConversationFlow
        squareNumber={landedSquare}
        theme={theme}
        userProfile={hasPersonalizedBoard && boardProfile ? mapQuestionnaireToBoardProfile(boardProfile) : DEFAULT_PROFILE}
        onClose={handleConversationClose}
      />
    );
  }

  // ── Mechanic card ─────────────────────────────────────────────────────────
  if (activeMechanic) {
    const mechDest = activeMechanic.toSquare ?? activeMechanic.to;
    const isSnake = activeMechanic.type === 'snake';
    const applyMechanic = () => {
      if (mechDest) {
        const destTheme = themes[mechDest] ?? { title: `Square ${mechDest}`, desc: 'Continue your practice', type: 'practice', phase: 'foundation' };
        setLandedSquare(mechDest);
        setPosition(mechDest);
        setTheme(destTheme);
        if (currentUser) {
          setDoc(doc(db, 'users', currentUser.uid, 'progress', 'data'), {
            currentSquare: mechDest,
          }, { merge: true }).catch(console.error);
        }
      }
      setActiveMechanic(null);
    };
    return (
      <>
        <div style={styles.bg} />
        <div style={styles.fullCenter}>
          <div style={styles.mechanicCard}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>
              {isSnake ? '🐍' : '🪜'}
            </div>
            <h2 style={styles.mechanicTitle}>
              {isSnake ? 'A snake appears...' : 'A ladder rises!'}
            </h2>
            <p style={styles.mechanicReason}>{activeMechanic.reason}</p>
            <p style={styles.mechanicDesc}>{activeMechanic.description}</p>
            {mechDest && (
              <p style={{ fontSize: '13px', color: '#C9920A', textAlign: 'center', marginBottom: '20px', fontWeight: '600' }}>
                {isSnake ? `Moving back to Square ${mechDest}` : `Leaping forward to Square ${mechDest}`}
              </p>
            )}
            <button style={styles.continueBtn} onClick={applyMechanic}>
              {isSnake ? 'Accept the setback' : 'Take the leap'}
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Design board prompt ───────────────────────────────────────────────────
  if (showDesignPrompt) {
    return (
      <>
        <div style={styles.bg} />
        <div style={styles.fullCenter}>
          <div style={styles.mechanicCard}>
            <div style={{ fontSize: '52px', marginBottom: '16px' }}>🗺️</div>
            <h2 style={styles.mechanicTitle}>Personalize your board?</h2>
            <p style={styles.mechanicDesc}>
              Answer 3 quick questions to tailor the journey to your life. Takes about 2 minutes.
            </p>
            <button style={styles.continueBtn} onClick={() => { setShowDesignPrompt(false); onGoToQuestionnaire(); }}>
              Design my board
            </button>
            <button style={styles.skipPromptBtn} onClick={() => setShowDesignPrompt(false)}>
              Maybe later
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Cycle complete — begin new journey ────────────────────────────────────
  if (position >= 100 && !rollResult && !isRolling) {
    return (
      <>
        <div style={styles.bg} />
        <div style={styles.fullCenter}>
          <div style={styles.mechanicCard}>
            <div style={{ fontSize: '48px', marginBottom: '20px', letterSpacing: '4px' }}>✨</div>
            <h2 style={{ ...styles.mechanicTitle, color: '#C9920A' }}>Cycle Complete</h2>
            <p style={styles.mechanicDesc}>
              You have walked this board. The practice shifts you — not to an end point, but to a new beginning. Your challenges have evolved. So has your board.
            </p>
            <p style={{ fontSize: '12px', color: 'rgba(201,146,10,0.6)', textAlign: 'center', marginBottom: '28px', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Square 100 of 100 · Ready for the next cycle
            </p>
            <button style={styles.continueBtn} onClick={onGoToQuestionnaire}>
              Reassess and begin a new journey
            </button>
            <button style={styles.skipPromptBtn} onClick={onOpenDashboard}>
              View your journey
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Main game screen ──────────────────────────────────────────────────────
  return (
    <>
      <div style={styles.bg} />
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.title}>Mokshapatra</h1>
              <p style={styles.subtitle}>Roll the dice. Trust the journey.</p>
            </div>
            <div style={styles.headerActions}>
              {onOpenReflection && (
                <button style={styles.iconBtn} onClick={onOpenReflection}>🌙</button>
              )}
              <button style={styles.iconBtn} onClick={onOpenDashboard}>👤</button>
              <button style={styles.signOutBtn} onClick={() => signOut().catch(console.error)}>Sign out</button>
            </div>
          </div>

          {/* Position row */}
          <div style={styles.statusRow}>
            <div style={styles.positionBadge}>
              Square {position} · {getPhaseLabel(position)}
            </div>
          </div>

          {/* Progress bar */}
          <div style={styles.progressWrap}>
            <div style={{ ...styles.progressFill, width: `${position}%` }} />
          </div>
        </div>

        {/* Dice */}
        <div style={styles.diceSection}>
          <button
            style={{
              ...styles.diceBtn,
              ...(isRolling ? styles.diceBtnRolling : {}),
              ...(rollResult && !isRolling ? styles.diceBtnResult : {}),
            }}
            onClick={rollDice}
            disabled={isRolling || !!rollResult}
          >
            {isRolling
              ? <DiceFace value={rollingFace} />
              : rollResult
                ? <DiceFace value={rollResult} />
                : <span style={styles.diceIdle}>🎲</span>
            }
          </button>
          {!rollResult && !isRolling && (
            <p style={styles.rollHint}>Tap to roll</p>
          )}
          {rollResult && !isRolling && (
            <p style={styles.rollHint}>Rolled <strong style={{ color: '#C9920A' }}>{rollResult}</strong> — now at square {landedSquare}</p>
          )}
        </div>

        {/* Result card */}
        {rollResult && theme && !isRolling && (
          <div style={styles.resultCard}>
            <div style={styles.squareLabel}>Square {landedSquare}</div>
            <h2 style={styles.practiceTitle}>{theme.title}</h2>
            <p style={styles.practiceDesc}>{theme.desc}</p>

            <button style={styles.beginBtn} onClick={() => setShowConversation(true)}>
              Begin Practice
            </button>
          </div>
        )}

        {/* Footer nudge */}
        {!hasPersonalizedBoard && !rollResult && (
          <button style={styles.nudgeBtn} onClick={onGoToQuestionnaire}>
            Design my personal board →
          </button>
        )}

      </div>
      <style>{`
        @keyframes diceshake {
          0%   { transform: rotate(-10deg) scale(0.96); }
          25%  { transform: rotate(10deg)  scale(1.04); }
          50%  { transform: rotate(-7deg)  scale(0.97); }
          75%  { transform: rotate(7deg)   scale(1.03); }
          100% { transform: rotate(-10deg) scale(0.96); }
        }
        @keyframes dicepop {
          0%   { transform: scale(0.65); opacity: 0.6; }
          60%  { transform: scale(1.08); opacity: 1;   }
          100% { transform: scale(1);    opacity: 1;   }
        }
      `}</style>
    </>
  );
}

const styles = {
  bg: { position: 'fixed', inset: 0, background: '#0D0D2B', zIndex: -1 },

  loadingCenter: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  loadingText: {
    fontFamily: '"Cinzel", serif', color: '#C9920A', fontSize: '16px',
    letterSpacing: '1px', margin: 0,
  },

  fullCenter: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: '20px',
  },

  container: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: '20px 20px 80px',
  },

  header: { width: '100%', maxWidth: '480px', marginBottom: '28px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' },
  title: {
    fontFamily: '"Cinzel", serif', fontSize: '22px', fontWeight: '700',
    color: '#C9920A', margin: 0, letterSpacing: '1px',
  },
  subtitle: { fontSize: '12px', color: 'rgba(245,240,232,0.45)', margin: '4px 0 0', },
  headerActions: { display: 'flex', gap: '8px' },
  iconBtn: {
    background: 'rgba(201,146,10,0.1)', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '10px', padding: '8px 12px', fontSize: '18px',
    cursor: 'pointer', color: '#C9920A',
  },
  signOutBtn: {
    background: 'none', border: '1px solid rgba(245,240,232,0.15)',
    borderRadius: '8px', padding: '6px 12px', fontSize: '12px',
    cursor: 'pointer', color: 'rgba(245,240,232,0.45)',
    fontFamily: '"Lato", sans-serif', whiteSpace: 'nowrap',
  },

  statusRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' },
  positionBadge: {
    fontSize: '13px', color: 'rgba(245,240,232,0.6)',
    fontFamily: '"Lato", sans-serif',
  },
  streakBadge: {
    fontSize: '12px', fontWeight: '700', color: '#C9920A',
    background: 'rgba(201,146,10,0.12)', border: '1px solid rgba(201,146,10,0.3)',
    borderRadius: '20px', padding: '3px 10px',
  },

  progressWrap: {
    height: '4px', background: 'rgba(201,146,10,0.15)',
    borderRadius: '2px', overflow: 'hidden',
  },
  progressFill: { height: '100%', background: '#C9920A', borderRadius: '2px', transition: 'width 0.6s ease' },

  diceSection: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    marginBottom: '28px',
  },
  diceBtn: {
    width: '110px', height: '110px', borderRadius: '20px',
    background: '#1E1145', border: '2px solid rgba(201,146,10,0.4)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, transition: 'border-color 0.3s, box-shadow 0.3s',
  },
  diceBtnRolling: {
    animation: 'diceshake 0.12s ease-in-out infinite',
    border: '2px solid rgba(201,146,10,0.7)',
    cursor: 'default',
  },
  diceBtnResult: {
    animation: 'dicepop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
    border: '2px solid rgba(201,146,10,0.8)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.4), 0 0 24px rgba(201,146,10,0.2)',
  },
  diceIdle: { fontSize: '48px' },
  rollHint: { fontSize: '13px', color: 'rgba(245,240,232,0.45)', marginTop: '12px' },

  resultCard: {
    background: '#1E1145', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '20px', padding: '28px 24px',
    maxWidth: '480px', width: '100%',
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
  },
  squareLabel: {
    fontSize: '12px', fontWeight: '700', color: '#C9920A',
    letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px',
  },
  practiceTitle: {
    fontFamily: '"Cinzel", serif', fontSize: '20px', fontWeight: '700',
    color: '#F5F0E8', marginTop: 0, marginBottom: '12px', lineHeight: '1.3',
  },
  practiceDesc: {
    fontSize: '15px', color: 'rgba(245,240,232,0.7)',
    lineHeight: '1.6', marginBottom: '24px',
  },
  beginBtn: {
    width: '100%', padding: '15px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '16px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    fontFamily: '"Lato", sans-serif',
  },

  nudgeBtn: {
    marginTop: '24px', background: 'none',
    border: '1px solid rgba(201,146,10,0.3)', borderRadius: '10px',
    padding: '10px 20px', color: '#C9920A', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },

  // Mechanic card
  mechanicCard: {
    background: '#1E1145', border: '1px solid rgba(201,146,10,0.25)',
    borderRadius: '20px', padding: '40px 32px',
    maxWidth: '400px', width: '100%', textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  mechanicTitle: {
    fontFamily: '"Cinzel", serif', fontSize: '22px', fontWeight: '700',
    color: '#F5F0E8', marginTop: 0, marginBottom: '10px',
  },
  mechanicReason: {
    fontSize: '13px', fontWeight: '700', color: '#C9920A',
    letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '14px',
  },
  mechanicDesc: {
    fontSize: '15px', color: 'rgba(245,240,232,0.65)',
    lineHeight: '1.6', marginBottom: '28px',
  },
  continueBtn: {
    width: '100%', padding: '14px', background: '#C9920A',
    border: 'none', borderRadius: '12px', fontSize: '16px',
    fontWeight: '700', color: '#0D0D2B', cursor: 'pointer',
    fontFamily: '"Lato", sans-serif', marginBottom: '10px',
  },
  skipPromptBtn: {
    width: '100%', padding: '12px', background: 'none',
    border: '1px solid rgba(245,240,232,0.15)', borderRadius: '12px',
    fontSize: '14px', color: 'rgba(245,240,232,0.45)', cursor: 'pointer',
  },
};
